import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, of } from 'rxjs';
import { map } from 'rxjs/operators';
import * as ProgramSessionActions from './session.actions';
import { ProgramSessionService } from '@program/services/program-session.service';
import { ProgramSessionDto } from '@shared/dto';

@Injectable()
export class ProgramSessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly programSessionService = inject(ProgramSessionService);

  readProgramSessions$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramSessionActions.readProgramSessions),
    exhaustMap(({ programId }) => this.programSessionService.read({ programId }).pipe(
      map((data: ProgramSessionDto[]) => ProgramSessionActions.readProgramSessionsSuccess({ data })),
      catchError((error) => of(ProgramSessionActions.readProgramSessionsFailure({ error })))
    ))
  ));

  createProgramSession$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramSessionActions.createProgramSession),
    exhaustMap(({ programId, dto }) => this.programSessionService.create(dto, { programId }).pipe(
      map((session: ProgramSessionDto) => ProgramSessionActions.createProgramSessionSuccess({ session })),
      catchError((error) => of(ProgramSessionActions.createProgramSessionFailure({ error })))
    ))
  ));

  updateProgramSession$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramSessionActions.updateProgramSession),
    exhaustMap(({ programId, sessionId, dto }) => this.programSessionService.update({ programId, sessionId }, dto).pipe(
      map((session: Partial<ProgramSessionDto>) => ProgramSessionActions.updateProgramSessionSuccess({ session })),
      catchError((error) => of(ProgramSessionActions.updateProgramSessionFailure({ error })))
    ))
  ));

  deleteProgramSession$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramSessionActions.deleteProgramSession),
    exhaustMap(({ programId, sessionId }) => this.programSessionService.delete({ programId, sessionId }).pipe(
      map((session: ProgramSessionDto) => ProgramSessionActions.deleteProgramSessionSuccess({ session })),
      catchError((error) => of(ProgramSessionActions.deleteProgramSessionFailure({ error })))
    ))
  ));

  restoreProgramSession$ = createEffect(() => this.actions$.pipe(
    ofType(ProgramSessionActions.restoreProgramSession),
    exhaustMap(({ programId, sessionId }) => this.programSessionService.restore({ programId, sessionId }).pipe(
      map((session: ProgramSessionDto) => ProgramSessionActions.restoreProgramSessionSuccess({ session })),
      catchError((error) => of(ProgramSessionActions.restoreProgramSessionFailure({ error })))
    ))
  ));
}
